import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import {
  ArrowLeft,
  ArrowRight,
  Clock,
  BellRing,
  RefreshCcw,
  Sparkles,
} from 'lucide-react';
import HealthLoopLogo from '@/components/HealthLoopLogo';

/**
 * How HealthLoop Works — mobile-first, calm, non-clinical, emotionally intelligent.
 * Single column. Generous spacing. Short paragraphs. One dominant CTA.
 */
const HowItWorks = () => {
  const navigate = useNavigate();

  const goToCheckIn = () => navigate('/login');

  return (
    <div className="min-h-screen bg-white" data-testid="how-it-works-page">

      {/* Header — sticky for easy back-nav on mobile */}
      <div className="sticky top-0 z-10 bg-white/80 backdrop-blur border-b border-gray-100">
        <div className="max-w-xl mx-auto px-5 py-3 flex items-center justify-between">
          <button
            onClick={() => navigate(-1)}
            className="inline-flex items-center gap-1.5 text-sm font-medium text-gray-700 active:text-emerald-700 -ml-1 px-2 py-1.5 rounded-lg"
            aria-label="Go back"
            data-testid="how-back-btn"
          >
            <ArrowLeft className="h-5 w-5" />
            <span>Back</span>
          </button>
          <HealthLoopLogo size="sm" showText={true} />
        </div>
      </div>

      <div className="max-w-xl mx-auto px-6 pt-10 pb-32">

        {/* Hero */}
        <section className="mb-14" data-testid="how-hero">
          <p className="text-xs font-semibold text-emerald-700 uppercase tracking-[0.15em] mb-4">
            How HealthLoop works
          </p>
          <h1 className="text-[2rem] sm:text-4xl font-bold text-gray-900 leading-[1.15] tracking-tight mb-5">
            A 60-second habit, not another tracker.
          </h1>
          <p className="text-[17px] sm:text-lg text-gray-600 leading-[1.6]">
            HealthLoop is a calm daily companion. One small moment, every day,
            to notice how you&apos;re doing — without the pressure or the spreadsheets.
          </p>
        </section>

        {/* 1. Simple introduction */}
        <section className="mb-14" data-testid="section-intro">
          <h2 className="text-[22px] sm:text-2xl font-bold text-gray-900 mb-3">
            What it is
          </h2>
          <p className="text-[16px] sm:text-[17px] text-gray-700 leading-[1.7]">
            A 60-second daily check-in. Four small things: hydration, meals,
            movement, sleep — plus how you&apos;re really feeling today.
          </p>
          <p className="text-[16px] sm:text-[17px] text-gray-700 leading-[1.7] mt-3">
            That&apos;s the whole product. Nothing to learn, nothing to optimise.
          </p>
        </section>

        {/* 2. The daily experience */}
        <section className="mb-14" data-testid="section-daily">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 rounded-full bg-emerald-50 flex items-center justify-center">
              <Clock className="h-5 w-5 text-emerald-600" />
            </div>
            <h2 className="text-[22px] sm:text-2xl font-bold text-gray-900">
              The daily experience
            </h2>
          </div>
          <p className="text-[16px] sm:text-[17px] text-gray-700 leading-[1.7]">
            Open the app. Tap through four prompts. Note how you&apos;re feeling.
            You&apos;ll be done in under a minute — usually faster.
          </p>
          <p className="text-[16px] sm:text-[17px] text-gray-700 leading-[1.7] mt-3">
            No streaks shouted at you. No guilt-tripping reminders. No goals to hit.
            Just a quiet moment that&apos;s yours.
          </p>
        </section>

        {/* 3. Nudges that support, not intrude */}
        <section className="mb-14" data-testid="section-nudges">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 rounded-full bg-emerald-50 flex items-center justify-center">
              <BellRing className="h-5 w-5 text-emerald-600" />
            </div>
            <h2 className="text-[22px] sm:text-2xl font-bold text-gray-900">
              Nudges that support, not intrude
            </h2>
          </div>
          <p className="text-[16px] sm:text-[17px] text-gray-700 leading-[1.7]">
            If you forget, you might get one calm email — never more than one a day.
            Always short. Always kind. Always optional.
          </p>
          <p className="text-[16px] sm:text-[17px] text-gray-700 leading-[1.7] mt-3">
            You can pause emails anytime in your profile. The app respects that immediately.
          </p>
        </section>

        {/* 4. Missed day recovery */}
        <section className="mb-14" data-testid="section-recovery">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 rounded-full bg-emerald-50 flex items-center justify-center">
              <RefreshCcw className="h-5 w-5 text-emerald-600" />
            </div>
            <h2 className="text-[22px] sm:text-2xl font-bold text-gray-900">
              Missed a few days? No problem.
            </h2>
          </div>
          <p className="text-[16px] sm:text-[17px] text-gray-700 leading-[1.7]">
            Life gets full. If you&apos;re away for a couple of days, you&apos;ll get one
            soft email — no guilt, no catching up.
          </p>
          <p className="text-[16px] sm:text-[17px] text-gray-700 leading-[1.7] mt-3 italic">
            &quot;It&apos;s okay. Whenever you&apos;re ready.&quot;
          </p>
          <p className="text-[16px] sm:text-[17px] text-gray-700 leading-[1.7] mt-3">
            That&apos;s it. Pick up wherever you are. Your past wins are still yours.
          </p>
        </section>

        {/* 5. Why it works */}
        <section className="mb-14" data-testid="section-why">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 rounded-full bg-emerald-50 flex items-center justify-center">
              <Sparkles className="h-5 w-5 text-emerald-600" />
            </div>
            <h2 className="text-[22px] sm:text-2xl font-bold text-gray-900">
              Why it works
            </h2>
          </div>
          <p className="text-[16px] sm:text-[17px] text-gray-700 leading-[1.7]">
            Big health changes usually fail. Small, kind ones tend to last.
          </p>
          <p className="text-[16px] sm:text-[17px] text-gray-700 leading-[1.7] mt-3">
            HealthLoop is built on three simple ideas: <strong className="text-gray-900">small beats clever</strong>,
            <strong className="text-gray-900"> awareness comes before change</strong>, and
            <strong className="text-gray-900"> kind beats strict</strong>.
          </p>
          <p className="text-[16px] sm:text-[17px] text-gray-700 leading-[1.7] mt-3">
            One minute of attention, repeated, becomes part of how a week feels.
            That&apos;s the whole bet.
          </p>
        </section>

        {/* What Makes HealthLoop Different? — explicit differentiator
            against unrelated products that share the "HealthLoop" name
            (hospital patient-monitoring, post-procedure recovery
            platforms, clinical care coordination tools). Reinforces the
            preventive-education positioning surfaced by the JSON-LD FAQ
            in /public/index.html. */}
        <section className="mb-14" data-testid="section-differentiator">
          <h2 className="text-[22px] sm:text-2xl font-bold text-gray-900 mb-3">
            What makes HealthLoop different?
          </h2>
          <p className="text-[16px] sm:text-[17px] text-gray-700 leading-[1.7]">
            HealthLoop focuses on <strong className="text-gray-900">prevention before illness occurs</strong>.
          </p>
          <p className="text-[16px] sm:text-[17px] text-gray-700 leading-[1.7] mt-3">
            Unlike clinical monitoring or hospital care-coordination
            platforms, HealthLoop empowers individuals through health
            education, wellness engagement, health literacy, and
            preventive guidance to support healthier everyday decisions.
          </p>
          <ul className="mt-5 grid sm:grid-cols-2 gap-2.5" aria-label="Core pillars">
            {[
              'Preventive Health',
              'Health Education',
              'Wellness Engagement',
              'Health Literacy',
              'Maternal &amp; Child Health',
              'Community Wellbeing',
              'Preventive Behaviour Change',
            ].map((pillar) => (
              <li
                key={pillar}
                className="inline-flex items-center gap-2 text-[14px] sm:text-[15px] text-gray-700"
              >
                <span className="inline-block w-1.5 h-1.5 rounded-full bg-emerald-500 shrink-0" />
                <span dangerouslySetInnerHTML={{ __html: pillar }} />
              </li>
            ))}
          </ul>

          {/* Sub-section: unambiguous denial of clinical-monitoring framing.
              Critical for search-engine differentiation against other
              "HealthLoop" products. */}
          <div
            className="mt-8 pt-6 border-t border-gray-100"
            data-testid="section-how-different"
          >
            <h3 className="text-lg sm:text-xl font-bold text-gray-900 mb-3">
              How HealthLoop is different
            </h3>
            <p className="text-[16px] sm:text-[17px] text-gray-700 leading-[1.7] mb-3">
              <strong className="text-gray-900">
                HealthLoop is not a hospital patient-monitoring platform.
              </strong>
            </p>
            <p className="text-[16px] sm:text-[17px] text-gray-700 leading-[1.7]">
              HealthLoop is a digital preventive health education platform
              designed to improve health awareness, encourage healthy
              behaviours, and support informed decision-making through
              accessible educational tools and wellness engagement features.
            </p>
          </div>
        </section>

        {/* Compliance footer copy */}
        <div className="mb-12 p-5 rounded-2xl bg-gray-50 border border-gray-100" data-testid="section-not">
          <p className="text-sm text-gray-600 leading-[1.7]">
            <strong className="text-gray-900">A note:</strong> HealthLoop is for
            awareness and daily habits — not diagnosis or medical advice.
            If you feel unwell, please speak to a qualified healthcare professional.
          </p>
        </div>

        {/* Closing tagline */}
        <p className="text-center text-[15px] text-gray-500 italic mb-2">
          Small steps today. Better health tomorrow.
        </p>
      </div>

      {/* Sticky bottom CTA — investor-grade mobile pattern */}
      <div
        className="fixed bottom-0 inset-x-0 bg-white/95 backdrop-blur border-t border-gray-100 px-5 py-4 pb-[max(1rem,env(safe-area-inset-bottom))]"
        data-testid="how-sticky-cta"
      >
        <div className="max-w-xl mx-auto">
          <Button
            onClick={goToCheckIn}
            className="w-full bg-emerald-600 hover:bg-emerald-700 active:bg-emerald-800 text-white h-14 text-[17px] font-semibold rounded-2xl shadow-lg shadow-emerald-100"
            data-testid="how-cta-btn"
          >
            Start daily check
            <ArrowRight className="ml-2 h-5 w-5" />
          </Button>
          <p className="text-center text-xs text-gray-500 mt-2">
            Takes less than 60 seconds · Free to try
          </p>
        </div>
      </div>

    </div>
  );
};

export default HowItWorks;
